import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, type MoltQueryKey } from './query-keys';
import {
  boardSockets,
  type BoardSubscriptionStatus,
  type LiveSocketFrame,
} from './socket';

export type BoardLiveStatus = BoardSubscriptionStatus | 'disabled';

export interface UseBoardLiveOptions {
  readonly boardId: string | undefined;
  readonly boardShortLink?: string | undefined;
  readonly openCardId?: string | undefined;
  readonly openCardShortLink?: string | undefined;
  readonly enabled: boolean;
}

export interface LiveBoardSupplementalInvalidationContext {
  readonly boardShortLink?: string | undefined;
  readonly openCardId?: string | undefined;
  readonly openCardShortLink?: string | undefined;
}

export function liveBoardSupplementalInvalidationKeys(
  frame: LiveSocketFrame,
  context: LiveBoardSupplementalInvalidationContext,
): MoltQueryKey[] {
  if (frame.type === 'comment.typing') return [];

  const keys: MoltQueryKey[] = [queryKeys.boards.pages()];
  if (context.boardShortLink !== undefined) {
    keys.push(queryKeys.boards.byShortLink(context.boardShortLink));
  }
  if (context.openCardId !== undefined && frame.cardId !== context.openCardId) {
    keys.push(queryKeys.cards.detail(context.openCardId));
  }
  if (context.openCardShortLink !== undefined) {
    keys.push(queryKeys.cards.byShortLink(context.openCardShortLink));
  }
  return keys;
}

function invalidateKeys(
  queryClient: ReturnType<typeof useQueryClient>,
  keys: readonly MoltQueryKey[],
): void {
  for (const queryKey of keys) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

function frameChangesBoard(frame: LiveSocketFrame, boardId: string): boolean {
  return frame.boardId === boardId;
}

export function useBoardLive({
  boardId,
  boardShortLink,
  openCardId,
  openCardShortLink,
  enabled,
}: UseBoardLiveOptions): BoardLiveStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<BoardLiveStatus>('disabled');
  const previousStatus = useRef<BoardLiveStatus>('disabled');

  useEffect(() => {
    previousStatus.current = 'disabled';

    if (!enabled || boardId === undefined) {
      setStatus('disabled');
      return undefined;
    }

    const subscription = boardSockets.subscribe({
      queryClient,
      boardId,
      onStatusChange: (next) => {
        const previous = previousStatus.current;
        previousStatus.current = next;
        setStatus(next);
        if (previous === 'reconnecting' && next === 'open') {
          invalidateKeys(queryClient, [
            queryKeys.boards.pages(),
            ...(boardShortLink === undefined
              ? []
              : [queryKeys.boards.byShortLink(boardShortLink)]),
            ...(openCardId === undefined ? [] : [queryKeys.cards.detail(openCardId)]),
            ...(openCardShortLink === undefined
              ? []
              : [queryKeys.cards.byShortLink(openCardShortLink)]),
          ]);
        }
      },
      onFrame: (frame) => {
        if (frameChangesBoard(frame, boardId)) {
          invalidateKeys(
            queryClient,
            liveBoardSupplementalInvalidationKeys(frame, {
              boardShortLink,
              openCardId,
              openCardShortLink,
            }),
          );
        }
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [boardId, boardShortLink, enabled, openCardId, openCardShortLink, queryClient]);

  return enabled && boardId !== undefined ? status : 'disabled';
}
